import { Flex, Typography } from 'antd';
import Button from 'components/Button';
import { useEffect, useMemo } from 'react';

import { CloseOutlined } from '@ant-design/icons';

import { useDispatch, useSelector } from 'react-redux';
import { getDistrictList } from '../../../../store/pipes/action';
import FilterPill from './FilterPill';
import FilterSection from './FilterSection';

import useAgronomistList from 'hooks/useAgronomistList';
import { useTranslation } from 'react-i18next';
import {
  selectDistrictList,
  selectIsDistrictLoading,
} from '../../../../store/pipes/selectors';
import { PIPE_STATUS } from '../../../../utils/constants';

const { Title } = Typography;

const FilterPopup = ({
  country,
  appliedFilters,
  onFilterChange,
  onFilterClear,
}) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const { isLoading: isAgronomistLoading, agronomistFilterOptions } =
    useAgronomistList(country);

  const districtList = useSelector(selectDistrictList);
  const isDistrictListLoading = useSelector(selectIsDistrictLoading);

  const isClearDisabled = useMemo(
    () => appliedFilters.length <= 0,
    [appliedFilters.length],
  );

  const districtOptions = useMemo(() => {
    const districtValue = districtList?.value && JSON.parse(districtList.value);
    const districtData = districtValue?.data?.district || {};

    const districts = Object.values(districtData)?.reduce((acc, curr) => {
      const list = curr
        ?.split(',')
        ?.map((d) => ({ type: 'district', label: d, value: d }));
      acc = acc.concat(list);
      return acc;
    }, []);

    return districts;
  }, [districtList]);

  const isFilterSelected = (filter) =>
    appliedFilters.find(
      (f) => f?.value === filter?.value && f?.type === filter?.type,
    );

  useEffect(() => {
    dispatch(getDistrictList(country));
  }, [dispatch, country]);

  return (
    <Flex vertical gap={20} className="filter-popup-container">
      <Flex align="center" gap={5} justify="space-between" flex={1}>
        <Title level={4} className="mt-2">
          {t('common.filters.filter')}
        </Title>
        <Button
          type="primary"
          icon={<CloseOutlined />}
          ghost
          disabled={isClearDisabled}
          onClick={onFilterClear}
        >
          {t('common.filters.clearFilters')}
        </Button>
      </Flex>

      <Flex flex={1} gap={20} className="filter-section-container">
        <FilterSection
          header={t('common.filters.agronomist')}
          isFilterLoading={isAgronomistLoading}
          options={agronomistFilterOptions}
        >
          {agronomistFilterOptions?.map((filter) => (
            <FilterPill
              key={filter.value}
              label={filter.label}
              onClick={() => onFilterChange(filter)}
              isSelected={isFilterSelected(filter)}
            />
          ))}
        </FilterSection>

        <FilterSection
          header={t('common.filters.status')}
          options={PIPE_STATUS}
        >
          {PIPE_STATUS.map((filter) => (
            <FilterPill
              key={filter.value}
              label={filter.label}
              onClick={() => onFilterChange(filter)}
              isSelected={isFilterSelected(filter)}
            />
          ))}
        </FilterSection>

        <FilterSection
          header={t('common.filters.district')}
          isFilterLoading={isDistrictListLoading}
          options={districtOptions}
        >
          {districtOptions?.map((filter) => (
            <FilterPill
              key={filter.value}
              label={filter.label}
              onClick={() => onFilterChange(filter)}
              isSelected={isFilterSelected(filter)}
            />
          ))}
        </FilterSection>
      </Flex>

      <Flex
        gap={5}
        flex={1}
        align="center"
        className="filter-section-container"
      >
        {appliedFilters?.map((filter, idx) => (
          <FilterPill
            isSelected
            key={`applied-filter-${filter.value}-${idx}`}
            label={filter.label}
            onClick={() => onFilterChange(filter)}
            icon={<CloseOutlined />}
            block={false}
            size={'large'}
          />
        ))}
      </Flex>
    </Flex>
  );
};

export default FilterPopup;
